import { createContext, PropsWithChildren, useContext, useEffect, useRef } from 'react';
import { CddQRPopupComponent, usePopup } from 'scbk-shared-module';
import DarkModePopupComponent from 'scbk-shared-module/modules/ComfyBank/DarkModePopupComponent';
import { DarkModeResult } from '../types/DarkModeResult';
import UserInfo from 'scbk-shared-module/layout/components/UserInfo';
import { Envs, useEnvContext } from 'scbk-services';

interface ContextProps {
  openCddQRPop: () => void;
  openDarkModePopup: () => Promise<DarkModeResult>;
  openUserInfoPopup: () => void;
}

const GlobalPopupContext = createContext<ContextProps>({
  openCddQRPop: () => {},
  openDarkModePopup: async () => Promise.resolve({ darkChange: 'N', noticeShowYn: 'N' }),
  openUserInfoPopup: () => {},
});

export const useGlobalPopupContext = () => useContext(GlobalPopupContext);

export const GlobalPopupProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { getEnv } = useEnvContext();

  // CDD 팝업 제어 객체
  const cddQRPopup = usePopup('CddQRPopupComponent');

  // 다크모드 팝업 결과 실행 함수 ref
  const resolveRef = useRef<(value: DarkModeResult) => void | undefined>(undefined);

  const darkModePopup = usePopup('DarkModePopupComponent');

  const userInfoPopup = usePopup('UserInfo');

  const userInfoCountRef = useRef<number>(0);

  /**
   * CDD QR 팝업 노출
   */
  const openCddQRPop = async () => {
    cddQRPopup.setTrigger();
    cddQRPopup.open();
  };

  /**
   * 다크모드 적용 팝업
   */
  const openDarkModePopup = async () => {
    darkModePopup.open();

    return new Promise<DarkModeResult>((resolve) => {
      resolveRef.current = resolve;
    });
  };

  /**
   * 테스트용 팝업
   */
  const openUserInfoPopup = async () => {
    const env = await getEnv();
    if (env === Envs.UAT || env === Envs.PROD) {
      return;
    }

    console.log('### userInfoCountRef.current : ', userInfoCountRef.current);
    userInfoCountRef.current += 1;

    if (userInfoCountRef.current >= 5) {
      userInfoCountRef.current = 0;
      userInfoPopup.open();
    }
  };

  /**
   *
   * @param DarkModeResult
   */
  const handleComplete = (darkModeResult: DarkModeResult) => {
    if (resolveRef.current) {
      resolveRef.current(darkModeResult);
    }
  };

  useEffect(() => {
    if (!resolveRef.current) {
      resolveRef.current = undefined;
    }
  }, []);

  return (
    <GlobalPopupContext.Provider value={{ openCddQRPop, openDarkModePopup, openUserInfoPopup }}>
      {children}
      {/* CDD QR 팝업 오픈 */}
      {cddQRPopup.isOpen && (
        <CddQRPopupComponent
          {...cddQRPopup}
          isOpen={cddQRPopup.isOpen}
          setOpen={cddQRPopup.setOpen}
        />
      )}
      {darkModePopup.isOpen && (
        <DarkModePopupComponent
          {...darkModePopup}
          isOpen={darkModePopup.isOpen}
          setOpen={darkModePopup.setOpen}
          onComplete={handleComplete}
        />
      )}
      <UserInfo
        {...userInfoPopup}
        isOpen={userInfoPopup.isOpen}
        setOpen={userInfoPopup.setOpen}
        onComplete={() => {}}
      />
    </GlobalPopupContext.Provider>
  );
};
